import { createRouter, createWebHistory } from 'vue-router'

import { hasClientClaim, isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        }
        if (from.path === to.path) {
            return {}
        }
        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        {
            path:     '/',
            redirect: '/dashboard',
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/select-client',
            name:      'select-client',
            component: () => import('@/views/SelectClient.vue'),
            meta:      { layout: 'Blank', requiresAuth: true },
        },
        {
            path:      '/dashboard',
            name:      'dashboard',
            component: () => import('@/views/Dashboard.vue'),
            meta:      { requiresAuth: true, requiresClient: true },
        },
        {
            path:      '/holders',
            name:      'holders',
            component: () => import('@/views/holder/Holders.vue'),
            meta:      { requiresAuth: true, requiresClient: true },
        },
        {
            path:      '/holders/:uuid',
            name:      'holder',
            component: () => import('@/views/holder/Holder.vue'),
            meta:      { requiresAuth: true, requiresClient: true },
        },
        {
            path:      '/ledger',
            name:      'ledger',
            component: () => import('@/views/ledger/Ledger.vue'),
            meta:      { requiresAuth: true, requiresClient: true },
        },
        {
            path:      '/value-types',
            name:      'value-types',
            component: () => import('@/views/value-type/ValueTypes.vue'),
            meta:      { requiresAuth: true, requiresClient: true },
        },
        {
            path:      '/venues',
            name:      'venues',
            component: () => import('@/views/venue/Venues.vue'),
            meta:      { requiresAuth: true, requiresClient: true },
        },
        {
            path:      '/devices',
            name:      'devices',
            component: () => import('@/views/device/Devices.vue'),
            meta:      { requiresAuth: true, requiresClient: true },
        },
    ],
})

router.beforeEach((to, from, next) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()
    const picked   = loggedIn && hasClientClaim(appStore.getJWT())

    // An authenticated user never sees the login page: send them on to their client or the picker.
    if (loggedIn && to.name === 'login') {
        return next(picked ? { name: 'dashboard' } : { name: 'select-client' })
    }

    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()
        return next({ name: 'login' })
    }

    // A data-plane route needs a client_slug token; an identity-only session must pick a client.
    if (to.meta.requiresClient && loggedIn && !picked) {
        return next({ name: 'select-client' })
    }

    next()
})

export default router
